<template>
  <div class="chart-card">
    <div class="chart-card__header">
      <div>
        <h3 class="chart-card__title">{{ title }}</h3>
        <p class="chart-card__subtitle">{{ subtitle }}</p>
      </div>
    </div>
    <div class="chart-card__body">
      <div v-if="!hasData" class="chart-card__empty">
        <font-awesome-icon icon="calendar-week" class="chart-card__empty-icon" />
        <p>Ingen data tilgjengelig</p>
      </div>
      <apexchart
        v-else
        type="bar"
        :height="height"
        :options="chartOptions"
        :series="series"
      />
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';

export default {
  name: 'DayOfWeekChart',
  components: { apexchart: VueApexCharts },
  props: {
    title: { type: String, default: 'Salg per ukedag' },
    subtitle: { type: String, default: 'Gjennomsnittlig omsetning' },
    labels: { type: Array, default: () => ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'] },
    data: { type: Array, default: () => [] },
    height: { type: Number, default: 280 }
  },
  computed: {
    hasData() {
      return this.data && this.data.length > 0 && this.data.some(v => v > 0);
    },
    maxValue() {
      return Math.max(...this.data);
    },
    series() {
      return [{
        name: 'Omsetning',
        data: this.data
      }];
    },
    chartOptions() {
      const self = this;
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          fontFamily: 'inherit'
        },
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: '60%',
            distributed: true,
            dataLabels: {
              position: 'top'
            }
          }
        },
        colors: this.data.map((val, idx) => {
          // Highlight best performing days
          if (val === this.maxValue) return '#B30E10';
          if (idx >= 4) return '#FF6B6B'; // Weekend colors
          return '#4ECDC4';
        }),
        dataLabels: {
          enabled: true,
          formatter: function(val) {
            return self.formatCompact(val);
          },
          offsetY: -20,
          style: {
            fontSize: '11px',
            fontWeight: 600,
            colors: ['var(--bo-ink)']
          }
        },
        legend: { show: false },
        xaxis: {
          categories: this.labels,
          labels: {
            style: {
              colors: 'var(--bo-muted)',
              fontSize: '12px'
            }
          },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: {
          labels: {
            formatter: function(val) {
              return self.formatCompact(val);
            },
            style: {
              colors: 'var(--bo-muted-2)',
              fontSize: '11px'
            }
          }
        },
        grid: {
          borderColor: 'var(--bo-surface-2)',
          strokeDashArray: 4
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return new Intl.NumberFormat('nb-NO').format(Math.round(val)) + ' kr';
            }
          }
        }
      };
    }
  },
  methods: {
    formatCompact(value) {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M';
      } else if (value >= 1000) {
        return (value / 1000).toFixed(0) + 'k';
      }
      return Math.round(value);
    }
  }
};
</script>

<style scoped>
.chart-card {
  background: var(--bo-surface);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.chart-card__header {
  padding: 20px 20px 0 20px;
}

.chart-card__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--bo-ink);
  margin: 0 0 4px 0;
}

.chart-card__subtitle {
  font-size: 13px;
  color: var(--bo-muted);
  margin: 0;
}

.chart-card__body {
  padding: 16px 20px 20px;
}

.chart-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: var(--bo-muted-2);
}

.chart-card__empty-icon {
  font-size: 32px;
  margin-bottom: 12px;
}
</style>
